import { createFileRoute } from '@tanstack/react-router'
import { useDeferredValue } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Distribution } from './-components/shared/distribution'
import { DistributionPending } from './-components/shared/distribution-pending'
import { trpc } from '@/main'
import { useRootSearch } from '@/hooks/use-root-search'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { UsersIcon } from 'lucide-react'
import { useLingui } from '@lingui/react/macro'

export const Route = createFileRoute('/_app/orgs/$orgId/groups/$groupId/distribution')({
  loaderDeps: ({ search }) => ({ orgType: search.orgType }),
  loader: ({ context, deps, params }) => {
    context.queryClient.prefetchQuery(
      trpc.orgs.getOrgGroupProfile.queryOptions({
        orgId: params.orgId,
        groupId: params.groupId,
        orgType: deps.orgType,
      }),
    )
  },
  component: RouteComponent,
  pendingComponent: DistributionPending,
  // Shown immediately: at the router's default pendingMs the NAV STACK cannot push while the loaders run,
  // because the level it would push is the route being rendered — see the full story in jobs.$jobId.report.
  pendingMs: 0,
  pendingMinMs: 0,
})

function RouteComponent() {
  const { t } = useLingui()
  const { orgId, groupId } = Route.useParams()
  const { second } = useRootSearch()

  if (!second?.id) {
    return (
      <PanelEmptyState
        icon={<UsersIcon className="text-muted-foreground size-5" />}
        title={t`Select a user`}
        description={t`Select a user from the list to view their profile.`}
      />
    )
  }

  return <DistributionLoaded orgId={orgId} groupId={groupId} />
}

function DistributionLoaded(props: { orgId: string; groupId: string }) {
  const { orgType } = useRootSearch()
  const deferredOrgType = useDeferredValue(orgType)
  const { data: orgGroupProfile } = useSuspenseQuery(
    trpc.orgs.getOrgGroupProfile.queryOptions({ orgId: props.orgId, groupId: props.groupId, orgType: deferredOrgType }),
  )

  return <Distribution profiles={orgGroupProfile?.profiles ?? []} data={orgGroupProfile?.distribution} />
}
